import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  IconButton,
  Avatar,
  Box,
} from '@mui/material';
// components
import { ConfirmationDialog } from '../../components/general/ConfirmationDialog';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import Iconify from '../../components/Iconify';
import SearchNotFound from '../../components/SearchNotFound';
import useQuery from '../../hooks/useQuery';
import EmployeeAssistanceModal from '../../sections/@dashboard/employees/AssistanceByEmployeeModal';
import { ListHead, ListToolbar, MoreMenu } from '../../components/tables';
import { useApi } from '../../hooks/useApi';
import SortDropdown from '../../components/SortDropdown';
import tracker from '../../api/tracker';

const override = {
  margin: '0 auto',
};

export default function EmployeeIndex() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState();
  const [pagination, setPagination] = useState({ total: 0, per_page: 0, current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [filterScope, setFilterScope] = useState('all');
  const [sort, setSort] = useState('asc');
  const [sortField, setSortField] = useState('name');
  const [selected, setSelected] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [employeeCount, setEmployeeCount] = useState(-1);
  const { doDelete } = useApi();
  const [loading, setLoading] = useState(true);

  const fetchData = useQuery();
  const { t } = useTranslation();

  const TABLE_HEAD = [
    { id: 'name', label: t('Name'), alignRight: false },
    { id: 'role', label: t('role'), alignRight: false, sortable: false },
    { id: 'personal_id', label: t('personal_id'), alignRight: false },
    { id: 'see_attendance', label: t('see_attendance'), sortable: false, align: 'center' },
    { id: 'actions', label: t('actions'), alignRight: true, sortable: false },
  ];

  const filterOptions = [
    { value: 'all', label: t('all') },
    { value: 'isRegular', label: t('regular') },
    { value: 'isNzv', label: t('is_nzv') },
    { value: 'showedUp', label: t('showed_up') },
    { value: 'didntShowUp', label: t('didnt_showed_up') },
  ];

  const refreshTable = async () => {
    setIsLoading(true);
    await fetchData('employees', filter, sort, setEmployees, sortField, setPagination, page, setNotFound, filterScope);
    setIsLoading(false);
  };
  const refreshCount = async () => {
    const result = await tracker.get('employees/count/assisted');
    setEmployeeCount(result.data.data);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(refreshTable, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [filter]);

  useEffect(() => {
    refreshTable();
  }, [sort, page, sortField, filterScope]);

  useEffect(() => {
    refreshCount();
  }, []);

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = employees.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleDeleteDialog = (employee) => {
    setSelectedEmployee(employee);
    setShowDeleteDialog(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async () => {
    setLoading(true);
    doDelete(`employees/${selectedEmployee.id}`, t('delete_success'), t('delete_error'), refreshTable);
    setLoading(false);
    setShowDeleteDialog(false);
    setSelected([]);
  };

  const handleMassDelete = async () => {
    setLoading(true);
    doDelete(`employees/mass`, t('delete_mass_success'), t('delete_mass_error'), refreshTable, { ids: selected }, true);
    setLoading(false);
    setSelected([]);
  };

  const handleFilterByName = (event) => {
    setFilter(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - employees.length) : 0;

  return (
    <Page title={t('Employees')}>
      <EmployeeAssistanceModal
        isShowing={showCalendarModal}
        employee={selectedEmployee}
        onClose={() => {
          setShowCalendarModal(false);
          setSelectedEmployee(selectedEmployee);
        }}
      />
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" gutterBottom>
            {t('Employees')}
          </Typography>
        </Stack>
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, mb: 3, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ fontStyle: 'italic', ml: 3 }}>
            {employeeCount > -1 && `Han asistido hoy: ${employeeCount}`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ListToolbar
                numSelected={selected.length}
                filterName={filter}
                onFilterName={handleFilterByName}
                placeholder={`search_employees`}
                handleMassDelete={handleMassDelete}
              />
              <SortDropdown size="small" options={filterOptions} sort={filterScope} setSort={setFilterScope} sx={{ ml: 2 }} />
            </Box>
            <Button
              variant="contained"
              component={RouterLink}
              to="/dashboard/employees/save"
              startIcon={<Iconify icon="eva:plus-fill" />}
              size="small"
              sx={{ height: 40 }}
            >
              {t('new_employee')}
            </Button>
          </Box>
        </Box>
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                {isLoading ? (
                  <FadeLoader color={'blue'} loading={loading} cssOverride={override} size={50} />
                ) : (
                  <>
                    <ListHead
                      headLabel={TABLE_HEAD}
                      rowCount={employees.length}
                      order={sort}
                      orderBy={sortField}
                      omitCheckbox
                      onRequestSort={setSortField}
                      setSort={setSort}
                    />
                    <TableBody>
                      {employees.map((row) => {
                        const {
                          fullname,
                          id,
                          role,
                          photos,
                          is_incomplete: isIncomplete,
                          showed_up_today: showedUpToday,
                        } = row;
                        const isItemSelected = selected.indexOf(id) !== -1;
                        const picture = photos ? photos[0] : null;
                        let backgroundColor;
                        if (isIncomplete) backgroundColor = '#ffcccb';
                        else if (showedUpToday) backgroundColor = '#cefad0';
                        else backgroundColor = 'white';
                        return (
                          <TableRow
                            hover
                            key={id}
                            tabIndex={-1}
                            role="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                            style={{ backgroundColor }}
                          >
                            <TableCell component="th" scope="row" padding="none" sx={{ pl: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar alt={fullname} src={picture ? picture.url : null} />
                                <Typography variant="subtitle2" noWrap>
                                  {fullname}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="left">{t(role)}</TableCell>
                            <TableCell align="left">{row.personal_id}</TableCell>
                            <TableCell align="center">
                              <IconButton onClick={() => { setSelectedEmployee(row); setShowCalendarModal(true); }}>
                                <Iconify icon="solar:calendar-bold-duotone" width={20} height={20} />
                              </IconButton>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton component={RouterLink} to={`/dashboard/employees/edit/${id}`}>
                                <Iconify icon="eva:edit-fill" width={20} height={20} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>

                    {notFound && (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                            <SearchNotFound searchQuery={filter} />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            component="div"
            rowsPerPageOptions={[]}
            count={pagination.total}
            rowsPerPage={pagination.per_page}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <ConfirmationDialog
            open={showDeleteDialog}
            onClose={() => {
              setShowDeleteDialog(false);
            }}
            onConfirm={handleDelete}
            message={t('delete_confirm')}
          />
        </Card>
      </Container>
    </Page>
  );
}
